import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

const Icon = ({ name, className = "" }) => {
  const common = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    className,
  };

  switch (name) {
    case "bell":
      return (
        <svg {...common}>
          <path
            d="M15 17H5l1.2-1.4A2 2 0 0 0 6.7 14V10a4.3 4.3 0 1 1 8.6 0v4c0 .5.2 1 .5 1.4L17 17h-2"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.5 19a2 2 0 0 0 3 0"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "dots":
      return (
        <svg {...common}>
          <path
            d="M12 13a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Z"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1"
          />
          <path
            d="M12 7a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Z"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1"
          />
          <path
            d="M12 19a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Z"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1"
          />
        </svg>
      );
    default:
      return null;
  }
};

export default function AppTopSection({
  title,
  subtitle,
  homePath = "/app/home",
  onNotify,
  onSettings,
  hasUnread = false,
}) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Nome dinâmico da pessoa logada
  const [userName, setUserName] = useState(() => {
    try {
      const rawUser = localStorage.getItem("user");
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed?.name) return parsed.name.trim().split(" ")[0];
      }
      const rawProfile = localStorage.getItem("userProfile");
      if (rawProfile) {
        const parsed = JSON.parse(rawProfile);
        if (parsed?.displayName) return parsed.displayName.trim().split(" ")[0];
      }
    } catch {
      // sem ação
    }
    return "";
  });

  useEffect(() => {
    async function fetchProfileName() {
      try {
        const res = await api.get("/users/profile");
        const p = res?.data?.profile;
        if (p?.displayName) {
          setUserName(p.displayName.trim().split(" ")[0]);
        } else if (p?.name) {
          setUserName(p.name.trim().split(" ")[0]);
        }
      } catch {
        // sem ação
      }
    }
    fetchProfileName();
  }, []);

  // Fechar menu ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const executeDirectLogout = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setMenuOpen(false);
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // sem ação
    }
    window.location.replace("/");
  };

  // Formatação amigável do título com "Olá [Nome]"
  const displayTitle = useMemo(() => {
    const raw = String(title || "").trim();
    if (raw.toLowerCase() === "olá" || raw.toLowerCase().startsWith("olá")) {
      return userName ? `Olá, ${userName}` : "Olá";
    }
    return raw;
  }, [title, userName]);

  return (
    <section className="relative overflow-visible rounded-[24px] bg-gradient-to-br from-[#6C54D8] via-[#5A45BF] to-[#24152F] py-6 px-5 sm:px-6 min-h-[96px] text-white shadow-md border border-[#ECE7FA]/20 mb-4 transition-all duration-300 z-40 flex flex-col justify-center">
      {/* Container isolado de fundo decorativo com overflow-hidden */}
      <div className="absolute inset-0 rounded-[24px] overflow-hidden pointer-events-none">
        <div className="absolute -right-8 -bottom-8 h-36 w-36 rounded-full bg-[#CBF1DB]/20 blur-2xl pointer-events-none" />
        <div className="absolute right-20 top-0 h-24 w-24 rounded-full bg-[#6C54D8]/30 blur-xl pointer-events-none" />
      </div>

      {/* CONTEÚDO DO CARTÃO ROXO RETANGULAR */}
      <div className="relative z-10 flex items-center justify-between gap-3">
        {/* LADO ESQUERDO: LOGO 'm' E O TÍTULO 'Olá, Fulano' */}
        <div className="flex items-center gap-3.5 min-w-0">
          <button
            type="button"
            onClick={() => navigate(homePath)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 text-white font-black text-xl shadow-inner hover:bg-white/30 transition-all active:scale-95 cursor-pointer"
            title="Página Inicial"
          >
            m
          </button>

          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white font-serif truncate leading-tight">
              {displayTitle}
            </h1>
            {subtitle && (
              <p className="text-xs sm:text-sm text-[#ECE7FA] font-medium truncate leading-tight mt-1">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* LADO DIREITO: SINO DE NOTIFICAÇÃO + MENU 3 PONTOS DA CONTA */}
        <div className="relative flex items-center gap-2 text-white shrink-0" ref={menuRef}>
          {/* 1. LEMBRETE / NOTIFICAÇÃO */}
          <button
            type="button"
            onClick={onNotify}
            className="relative flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 transition-all active:scale-95 cursor-pointer text-white"
            title="Lembretes e Notificações"
          >
            <Icon name="bell" />
            {hasUnread && (
              <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-[#6C54D8] animate-pulse" />
            )}
          </button>

          {/* 2. MENU TRÊS PONTOS VERTICAIS (MEU PERFIL, CONFIGURAÇÕES, SAIR DA CONTA) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-all active:scale-95 cursor-pointer ${
                menuOpen
                  ? "bg-white text-[#24152F] shadow-md"
                  : "bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white"
              }`}
              title="Opções da conta"
            >
              <Icon name="dots" />
            </button>

            {/* DROPDOWN MENU FLUTUANTE SOBREPOSTO */}
            {menuOpen && (
              <div className="absolute right-0 top-12 z-[99999] min-w-[190px] rounded-2xl bg-white p-2 shadow-[0_10px_30px_rgba(36,21,47,0.2)] border border-[#ECE7FA] animate-in fade-in zoom-in-95 duration-150 text-[#24152F]">
                {/* 1. Meu perfil */}
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/app/profile");
                  }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-bold text-[#24152F] hover:bg-[#ECE7FA] hover:text-[#6C54D8] rounded-xl transition-all cursor-pointer text-left"
                >
                  <svg className="w-4 h-4 text-[#6C54D8] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Meu perfil</span>
                </button>

                {/* 2. Configurações */}
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    if (onSettings) onSettings();
                    else navigate("/app/settings");
                  }}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-bold text-[#24152F] hover:bg-[#ECE7FA] hover:text-[#6C54D8] rounded-xl transition-all cursor-pointer text-left"
                >
                  <svg className="w-4 h-4 text-[#6C54D8] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Configurações</span>
                </button>

                {/* Divisor visual */}
                <hr className="my-1.5 border-[#ECE7FA]" />

                {/* 3. Sair da conta */}
                <button
                  type="button"
                  onClick={executeDirectLogout}
                  onMouseDown={executeDirectLogout}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-all cursor-pointer text-left border border-red-100"
                >
                  <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Sair da conta</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}